import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import { useAuth } from '../../AuthProvider'
import {
  getSchemaDiff, getSchemaDiffProducts, decideSchemaDiffEntry, getSchemaDiffDecisions,
  runProbeOnboarding, getProbeOnboardingStatus, mergeIaCandidates, getMissingGetAllSiblings,
  autoPromoteGetAll, debugRawHitGet,
} from '../../api'
import { stage7OutcomePlain } from '../../utils/stage7Outcomes'
import './AdminSchemaDiff.css'
import LoadError from '../../components/LoadError'
import { loadErrorMessage } from '../../utils/loadState'

// Same xlsx pattern as AdminDevHubCapture.jsx's _exportSheetsToExcel (Scott, 2026-09-23:
// "add a way to dump the results being presented on the page to a spreadsheet so I don't
// have to do the F12 crap") -- dynamic import, only loaded when an export is clicked.
async function _exportRowsToExcel(filename, rows, sheetName = 'Sheet1') {
  if (!rows || !rows.length) return
  const XLSX = await import('xlsx')
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), sheetName)
  XLSX.writeFile(wb, filename)
}

// (method, path) alone is not a unique identity for ia_batch entries -- register #88's
// generated candidates (services/ia_entity_generator.py) all share the literal path
// /v1/commons/data/multi_read, so every one of them would otherwise render under the
// SAME React key and the SAME decided-state lookup (F-2026-09-05-05/06's collision,
// reproduced here in the UI). row.probe_name (present on every "new"/"changed" row) and
// d.probe_name (now recorded on every decision, routers/admin.py's decision_record)
// disambiguate identically -- fall back to the old (method, path) key when absent so
// ordinary, never-ambiguous endpoints and pre-existing decisions keep matching as before.
function rowKey(row) {
  return row.probe_name ? `${row.method}:${row.path}:${row.probe_name}` : `${row.method}:${row.path}`
}

function decisionKey(d) {
  return d.probe_name ? `${d.method}:${d.path}:${d.probe_name}` : `${d.method}:${d.path}`
}

// Scott, 2026-09-27, live: "we have to do something about the readability of
// this page because it blows" -- one live probe run put hundreds of rows in New alone,
// unfiltered and unpaginated, with nothing but scrolling to find anything in
// it. The outcome value here mirrors stage7Info's own vocabulary exactly
// (undefined/no badge reads as 'not_probed') so the filter dropdown and the
// badge the reader is actually looking at can never disagree about what a row
// currently shows.
function rowOutcomeKey(row) {
  const info = stage7Info(row)
  if (!info) return 'not_applicable'  // GET/simple row -- Stage 7 was never in play
  return info.outcome || 'not_probed'
}

function matchesRowFilter(row, searchText, outcomeFilter) {
  if (outcomeFilter !== 'all' && rowOutcomeKey(row) !== outcomeFilter) return false
  if (!searchText) return true
  const needle = searchText.toLowerCase()
  return (row.path || '').toLowerCase().includes(needle)
    || (row.probe_name || '').toLowerCase().includes(needle)
}

// Scott, 2026-09-27: hundreds of "New" rows landed after a live probe run and there was
// no way to get them off the page for review -- the only existing export
// (_exportRowsToExcel above) was wired to the missing-get-all-siblings panel alone.
// `diff.new`/`diff.changed` rows carry a nested `probe_entry` (the full captured spec
// entry) that `json_to_sheet` would render as "[object Object]" if passed raw, so this
// flattens each row to the same fields the table itself shows, plus the stage7 detail
// the table only shows as a badge.
function _flattenDiffRowForExport(row) {
  const s7 = stage7Info(row)
  return {
    Method: row.method,
    Path: row.path,
    'Probe name': row.probe_name,
    'Functional area': row.probe_entry?.functional_area || '',
    'Stage 7 classification': row.probe_entry?._provenance?.classification || '',
    'Stage 7 outcome': s7?.outcome || '',
    'Unresolved placeholders': (row.unresolved_placeholders || []).join(', '),
    Diffs: (row.diffs_plain || row.diffs || []).join(' | '),
  }
}

// Stage 7 status for one diff row, read from the same probe_entry._provenance the
// backend already stamps (services/probe_harness/emitter.py) -- not a second source
// of truth, just surfacing what compute_diff already carries but the table wasn't
// showing. `undefined` for anything not classified needs_body.
function stage7Info(row) {
  const prov = row.probe_entry?._provenance
  if (!prov || prov.classification !== 'needs_body') return undefined
  return {
    outcome: prov.stage7_outcome,             // undefined until a discovery round has run
    vendorNamed: prov.vendor_named || [],
    cannotDerive: prov.cannot_derive || [],
    marker: row.probe_entry?._needs_stage7 || 'body discovery required',
  }
}

function Stage7Badge({ info }) {
  if (!info) return null
  const label = info.outcome ? `needs_body — ${info.outcome}` : 'needs_body — not yet probed'
  const plain = info.outcome
    ? stage7OutcomePlain(info.outcome)
    : 'Hasn’t been probed yet — use "Run against live connection" above to have Stage 7 try it against a real customer connection.'
  const title = [info.marker,
    info.vendorNamed.length ? `vendor wants: ${info.vendorNamed.join(', ')}` : '',
    info.cannotDerive.length ? `cannot derive: ${info.cannotDerive.join(', ')}` : '']
    .filter(Boolean).join(' · ')
  return (
    <div className="sdr-stage7-cell">
      <span className="sdr-stage7-badge" title={title}>{label}</span>
      {plain && <p className="sdr-stage7-plain">{plain}</p>}
    </div>
  )
}

function DiffsList({ diffs, diffsPlain }) {
  return (
    <ul className="sdr-diffs">
      {diffs.map((d, i) => {
        const plain = diffsPlain?.[i]
        const isArtifact = d.includes('SPEC INT-BOUND ARTIFACT')
        return (
          <li key={i} className={isArtifact ? 'sdr-diff-artifact' : ''}>
            {plain ? <span className="sdr-diff-plain">{plain}</span> : d}
            {plain && <span className="sdr-diff-raw" title="raw diff, for reference">{d}</span>}
          </li>
        )
      })}
    </ul>
  )
}

function ActionButtons({ row, decidedState, onDecide, busy }) {
  if (decidedState) {
    return (
      <span className={`sdr-decided sdr-decided-${decidedState.decision}`}>
        {decidedState.decision === 'promote' ? '✓ promoted' : '– ignored'} by {decidedState.decided_by}
      </span>
    )
  }
  // Scott, 2026-09-26: "why do I have the ability to promote get_configuration_v1_earnings
  // for PRO with no stage 7 notes and when I hit promote I get API error 422?" The backend
  // ALREADY rejects this (decide_schema_diff_entry's own unresolved-path-placeholder check)
  // -- the bug was showing an active Promote button for a row that check was always going
  // to fail, discoverable only after a click. row.unresolved_placeholders is computed
  // server-side by the SAME check (services/schema_diff.py's compute_diff), so this can
  // never disagree with what a click would actually do.
  const placeholders = row.unresolved_placeholders || []
  if (placeholders.length > 0) {
    return (
      <div className="sdr-actions">
        <button className="sdr-btn sdr-btn-promote" disabled title={
          `Cannot promote -- path still has unresolved placeholder(s): ${placeholders.join(', ')}. `
          + 'Neither production sync path substitutes path segments, so this would send the '
          + 'literal brace string to the vendor and fail on every sync.'
        }>
          Promote
        </button>
        <button className="sdr-btn sdr-btn-ignore" disabled={busy} onClick={() => onDecide(row, 'ignore')}>
          Ignore
        </button>
        <p className="sdr-diff-plain" style={{ marginTop: 4 }}>
          Can't promote — unresolved path placeholder(s): {placeholders.join(', ')}. This
          endpoint needs Stage 7 (or a manual fix) to resolve these before it's promotable.
        </p>
      </div>
    )
  }
  return (
    <div className="sdr-actions">
      <button className="sdr-btn sdr-btn-promote" disabled={busy} onClick={() => onDecide(row, 'promote')}>
        Promote
      </button>
      <button className="sdr-btn sdr-btn-ignore" disabled={busy} onClick={() => onDecide(row, 'ignore')}>
        Ignore
      </button>
    </div>
  )
}

export default function AdminSchemaDiff() {
  const { profile } = useAuth()
  // item 145 (second occurrence): the OLD hardcoded PRODUCTS = [UKG Pro, UKG WFM]
  // pair -- the same anti-pattern already fixed on AdminDevHubCapture.jsx's
  // probe-run picker, one layer different here (per-PRODUCT, not per-customer-
  // connection: see getSchemaDiffProducts' own docstring / routers/admin.py's
  // list_schema_diff_products). `selected` starts null until this platform's
  // REAL list of diffable products loads, so nothing dereferences it early.
  const [products, setProducts] = useState([])
  // A failed products read is an error with a Retry, never "No diffable products found" (item 201).
  const [productsError, setProductsError] = useState(null)
  const [productsTry, setProductsTry] = useState(0)
  const [selected, setSelected] = useState(null)
  const [diff, setDiff] = useState(null)
  const [decisions, setDecisions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busyKey, setBusyKey] = useState(null)
  const [noteDraft, setNoteDraft] = useState('')

  // Readability fix, 2026-09-27 -- see rowOutcomeKey/matchesRowFilter above.
  // Shared by New and Changed: one filter bar, not two independent ones a
  // reader would have to set twice to look at the same slice in both.
  const [rowSearch, setRowSearch] = useState('')
  const [outcomeFilter, setOutcomeFilter] = useState('all')
  const filteredNew = useMemo(
    () => (diff?.new || []).filter(r => matchesRowFilter(r, rowSearch, outcomeFilter)),
    [diff, rowSearch, outcomeFilter],
  )
  const filteredChanged = useMemo(
    () => (diff?.changed || []).filter(r => matchesRowFilter(r, rowSearch, outcomeFilter)),
    [diff, rowSearch, outcomeFilter],
  )

  // Stage 7 — Body Discovery trigger. This re-runs the FULL onboarding probe
  // (auth + endpoint classification + shape/relationship/paging + Stage 7) against
  // a real customer connection; there is no per-endpoint trigger in the backend,
  // since _discover_bodies (runner.py) targets whatever the run itself classifies
  // needs_body, not a caller-supplied endpoint list. So this is a page-level
  // action, not a per-row one, even though the badges below are per-row.
  const [stage7CustomerId, setStage7CustomerId] = useState('')
  const [stage7Running, setStage7Running] = useState(false)
  const [stage7Status, setStage7Status] = useState(null)
  const [stage7Error, setStage7Error] = useState(null)
  const pollRef = useRef(null)

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current) }, [])

  // Merge IA candidates — item 31. Generates candidate declarations for UKG's IA
  // entities (config/schemas/UKG/wfm_ia_entity_catalog.json) never hand-adjudicated
  // into an endpoint, and merges them into the live probe emission so they show up as
  // "new" rows below. Always dry-runs first (reports what WOULD be added, writes
  // nothing) — the actual write is a separate, explicit action, same discipline as
  // Promote/Ignore itself.
  const [mergeRunning, setMergeRunning] = useState(false)
  const [mergeResult, setMergeResult] = useState(null)
  const [mergeError, setMergeError] = useState(null)

  // item 158 -- reads only what's already durably captured in Supabase Storage (every
  // domain any Dev Hub drain has EVER pulled for this vendor/product), no live probe run
  // and no vendor API round-trip. Scott, 2026-09-26: "every single one of these has an
  // 'All' option and here we are burning tokens, hours, money because you're not looking
  // at things wholistically." Page-level, like Merge IA Candidates above -- not tied to
  // one row.
  const [missingRunning, setMissingRunning] = useState(false)
  const [missingResult, setMissingResult] = useState(null)
  const [missingError, setMissingError] = useState(null)

  // Raw Hit -- a UI wrapper around debug-raw-hit (existed since §19.66, never
  // wired to any page). Scott: "I have no idea what debug raw hit means or
  // where that would happen." Deliberately GET-only (see debugRawHitGet's own
  // comment) -- for "is this endpoint even enabled for this tenant" spot
  // checks, e.g. against item 158's Missing Get-All list, not general use.
  // Reuses stage7CustomerId -- the same "which real connection" input Run
  // Stage 7 already asks for on this page.
  const [rawHitPath, setRawHitPath] = useState('')
  const [rawHitRunning, setRawHitRunning] = useState(false)
  const [rawHitResult, setRawHitResult] = useState(null)
  const [rawHitError, setRawHitError] = useState(null)

  async function handleRawHit() {
    if (!stage7CustomerId.trim()) {
      setRawHitError('customer_id is required -- use the same field as Run Stage 7 above.')
      return
    }
    if (!rawHitPath.trim()) {
      setRawHitError('a path is required, e.g. /v1/work/form_profiles')
      return
    }
    setRawHitError(null)
    setRawHitResult(null)
    setRawHitRunning(true)
    try {
      const result = await debugRawHitGet(
        stage7CustomerId.trim(), selected.vendor, selected.product, rawHitPath.trim(),
      )
      setRawHitResult(result)
    } catch (err) {
      setRawHitError(err.message || String(err))
    } finally {
      setRawHitRunning(false)
    }
  }

  async function handleFindMissingGetAll() {
    setMissingError(null)
    setMissingRunning(true)
    try {
      // Passing stage7CustomerId (when filled in) is what turns "structurally
      // missing" into "and here's what actually happened last time it was
      // probed against THIS tenant" -- optional, so this still works with no
      // customer picked, same as before.
      const result = await getMissingGetAllSiblings(
        selected.vendor, selected.product, stage7CustomerId.trim() || undefined,
      )
      setMissingResult(result)
    } catch (err) {
      setMissingError(err.message || String(err))
    } finally {
      setMissingRunning(false)
    }
  }

  // item 158's auto-promote carve-out. Scott, 2026-09-26: "I don't want you promoting 1
  // isolated endpoint... I'm bored with that 1 at a time nonsense and it's why nothing
  // ever gets completely resolved." dryRun always previews first -- the write is a
  // separate, explicit second click, same discipline as Merge IA Candidates above.
  const [autoPromoteRunning, setAutoPromoteRunning] = useState(false)
  const [autoPromoteResult, setAutoPromoteResult] = useState(null)
  const [autoPromoteError, setAutoPromoteError] = useState(null)

  async function handleAutoPromote(write) {
    setAutoPromoteError(null)
    setAutoPromoteRunning(true)
    try {
      const result = await autoPromoteGetAll(
        selected.vendor, selected.product, profile?.email || 'unknown', !write,
      )
      setAutoPromoteResult(result)
      if (write) await load(selected)   // pick up the newly-promoted endpoints
    } catch (err) {
      setAutoPromoteError(err.message || String(err))
    } finally {
      setAutoPromoteRunning(false)
    }
  }

  async function handleMergeIaCandidates(write) {
    setMergeError(null)
    setMergeRunning(true)
    try {
      const result = await mergeIaCandidates(selected.vendor, selected.product, !write)
      setMergeResult(result)
      if (write) await load(selected)   // pick up the newly-merged candidates
    } catch (err) {
      setMergeError(err.message || String(err))
    } finally {
      setMergeRunning(false)
    }
  }

  async function handleRunStage7() {
    if (!stage7CustomerId.trim()) {
      setStage7Error('customer_id is required — Stage 7 needs a real, live connection to converse with.')
      return
    }
    setStage7Error(null)
    setStage7Status(null)
    setStage7Running(true)
    let data
    try {
      data = await runProbeOnboarding(stage7CustomerId.trim(), selected.vendor, selected.product, { discoverBodies: true })
    } catch (err) {
      setStage7Error(err.message || String(err))
      setStage7Running(false)
      return
    }
    // Scott, 2026-09-24: "look everywhere for something like that instead of
    // us snipe hunting." This page is a SECOND, independent entry point into
    // the exact same debug-probe-onboarding backend feature
    // (AdminDevHubCapture.jsx is the first) -- reimplemented from scratch,
    // and this response was discarded entirely until now. The backend's own
    // concurrency guard (routers/connector.py's debug_probe_onboarding)
    // returns a 200 with started:false, not an error, when a run is already
    // in progress for this customer -- ignoring that meant clicking Run
    // Stage 7 while one was already running silently started polling an
    // EXISTING run's progress with no indication this click didn't actually
    // start anything, the exact class of gap items 119/120/124 already
    // closed for the other entry point but never reached this one.
    if (data?.started === false) {
      setStage7Error(data.note || 'A run is already in progress for this customer -- watching it instead of starting a new one.')
      if (data.existing_status) setStage7Status(data.existing_status)
    }
    pollRef.current = setInterval(async () => {
      try {
        const status = await getProbeOnboardingStatus(stage7CustomerId.trim(), selected.vendor, selected.product)
        setStage7Status(status)
        if (status.done) {
          clearInterval(pollRef.current)
          pollRef.current = null
          setStage7Running(false)
          if (!status.error) await load(selected)   // pick up any newly-proven bodies
        }
      } catch (err) {
        clearInterval(pollRef.current)
        pollRef.current = null
        setStage7Running(false)
        setStage7Error(err.message || String(err))
      }
    }, 5000)
  }

  const load = useCallback(async (target) => {
    setLoading(true)
    setError(null)
    try {
      const [diffRes, decisionsRes] = await Promise.all([
        getSchemaDiff(target.vendor, target.product),
        getSchemaDiffDecisions(target.vendor, target.product),
      ])
      setDiff(diffRes)
      setDecisions(decisionsRes.decisions || [])
    } catch (err) {
      setError(err.message || String(err))
      setDiff(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { if (selected) load(selected) }, [selected, load])

  // Switching products must not show a stale result from the PREVIOUS product's check.
  useEffect(() => {
    setMissingResult(null); setMissingError(null)
    setAutoPromoteResult(null); setAutoPromoteError(null)
  }, [selected])

  // This platform's REAL diffable products, not a hardcoded pair -- fetched once on
  // mount. Defaults to UKG WFM when present (matching this page's existing
  // muscle-memory default), else the first product found, so a fresh load always
  // lands on something real instead of an empty selector.
  useEffect(() => {
    let cancelled = false
    setProductsError(null)
    getSchemaDiffProducts().then(list => {
      if (cancelled) return
      setProducts(list || [])
      const preferred = (list || []).find(p => p.vendor === 'UKG' && p.product === 'UKG_PRO_WFM_REST') || list?.[0]
      if (preferred) setSelected(preferred)
    }).catch(err => { if (!cancelled) { setProducts([]); setProductsError(loadErrorMessage(err)) } })
    return () => { cancelled = true }
  }, [productsTry])

  // Most recent decision per (method, path), so a promoted/ignored row shows its outcome
  // instead of Promote/Ignore buttons again.
  const decidedByKey = {}
  for (const d of decisions) {
    decidedByKey[decisionKey(d)] = d
  }

  async function handleDecide(row, decision) {
    const key = rowKey(row)
    setBusyKey(key)
    try {
      await decideSchemaDiffEntry({
        vendor: selected.vendor,
        product: selected.product,
        path: row.path,
        method: row.method,
        decision,
        decidedBy: profile?.email || 'unknown',
        note: noteDraft || undefined,
        probeName: row.probe_name,
      })
      setNoteDraft('')
      await load(selected)
    } catch (err) {
      setError(err.message || String(err))
    } finally {
      setBusyKey(null)
    }
  }

  const breadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Schema Diff Review' },
  ]
  const navActions = <Link to="/admin" className="nav-btn">← Dashboard</Link>

  // Nothing below this point can render sensibly before a real product is
  // selected -- shown briefly on first load, or if this platform genuinely has
  // no diffable products yet (no config/schemas file has passed
  // _is_product_schema, an honest state to show rather than crash on).
  if (!selected) {
    return (
      <>
        <TopNav breadcrumbs={breadcrumbs} actions={navActions} />
        <div className="sdr-page">
          {productsError && <LoadError what="diffable products" error={productsError} onRetry={() => setProductsTry(n => n + 1)} />}
          <p className="sdr-subtitle">
            {productsError ? '' : products.length === 0 && !loading
              ? 'No diffable products found — no config/schemas file has a product identity yet.'
              : 'Loading products…'}
          </p>
        </div>
      </>
    )
  }

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />
      <div className="sdr-page">
        <div className="sdr-header">
          <div>
            <h1 className="sdr-title">Schema Diff Review</h1>
            <p className="sdr-subtitle">
              config/schemas/ vs. the live Developer Hub probe capture — item 21/22,
              master-build-plan.md
            </p>
          </div>
          <select
            className="sdr-select"
            value={`${selected.vendor}::${selected.product}`}
            onChange={e => {
              const [vendor, product] = e.target.value.split('::')
              setSelected(products.find(p => p.vendor === vendor && p.product === product))
            }}
          >
            {products.map(p => (
              <option key={`${p.vendor}::${p.product}`} value={`${p.vendor}::${p.product}`}>
                {p.vendor} / {p.product}
              </option>
            ))}
          </select>
        </div>

        {/* Scott, 2026-09-25: "I really don't understand what any of this stuff
            means. Is there a way you can explain it in english on the page?" --
            open by default (not tucked behind a click nobody makes on a first
            visit), plain language, no jargon left unexplained. */}
        <details className="sdr-explainer" open>
          <summary className="sdr-explainer-summary">What does this page mean?</summary>
          <div className="sdr-explainer-body">
            <p>
              <strong>Config</strong> is what this platform is currently set up to use when
              it talks to this vendor's API. The <strong>live capture</strong> is what we
              just observed by asking the vendor directly, right now, using "Run against live
              connection" below. This page compares the two.
            </p>
            <ul>
              <li><strong>New</strong> — the vendor has something the live capture reached that config doesn't know about at all yet.</li>
              <li><strong>Changed</strong> — config and the vendor agree this endpoint exists, but something about it disagrees. Each row explains what, in plain English, under its diff.</li>
              <li><strong>Unchanged</strong> — config and the live capture agree. Nothing to review.</li>
              <li><strong>Not in live capture</strong> — config has this endpoint, but the last probe run didn't happen to reach it this time. Not a removal — just not exercised yet.</li>
            </ul>
            <p>
              <strong>Promote</strong> adopts what the live capture observed into config
              (a real, committed change). <strong>Ignore</strong> records that a human looked
              at this and chose not to act on it, without changing config. Either one is
              reversible by a later decision.
            </p>
            <p>
              The amber <strong>needs_body</strong> badge means Stage 7's body discovery
              hasn't yet worked out what request the vendor wants for that endpoint — see the
              plain-English line under each badge for what happened the last time it tried.
            </p>
          </div>
        </details>

        <div className="sdr-stage7-panel">
          <div className="sdr-stage7-row">
            <span className="sdr-stage7-label">Customer connection</span>
            <input
              type="text"
              className="sdr-stage7-input"
              placeholder="customer_id with a real, saved connection (e.g. tenant_a)"
              value={stage7CustomerId}
              onChange={e => setStage7CustomerId(e.target.value)}
              disabled={stage7Running}
            />
          </div>
          <p className="sdr-hint">
            One customer_id, used by all three tools below it: Run Stage 7, Find missing
            "get all" endpoints, and Raw Hit. Fill it in once here.
          </p>
        </div>

        <div className="sdr-stage7-panel">
          <div className="sdr-stage7-row">
            <span className="sdr-stage7-label">Stage 7 — Body Discovery</span>
            <button className="sdr-btn sdr-btn-stage7" disabled={stage7Running} onClick={handleRunStage7}>
              {stage7Running ? 'Running…' : 'Run against live connection'}
            </button>
          </div>
          <p className="sdr-hint">
            Re-runs the full onboarding probe (this is not per-endpoint — the harness itself
            decides which endpoints are <code>needs_body</code> from the SAME run) and converses
            with every one using the vendor's own refusal until a real 2xx proves the body, or it
            gives up. Sends multiple live requests per endpoint — do not run against a customer's
            connection you don't mean to exercise. Only a proven, observed 2xx ever lands in the
            diff below; nothing is guessed. Uses the customer connection entered above.
          </p>
          {stage7Error && <div className="sdr-error">{stage7Error}</div>}
          {stage7Status && (
            <p className="sdr-stage7-status">
              {stage7Status.done
                ? (stage7Status.error
                  ? `Failed: ${stage7Status.error}`
                  : `Done. ${(stage7Status.body_discoveries || []).length} needs_body endpoint(s) conversed with — `
                    + `${(stage7Status.body_discoveries || []).filter(d => d.outcome === 'resolved').length} resolved. `
                    + 'Diff refreshed below.')
                : `In progress — stage: ${stage7Status.stage || 'starting'}`
                  + (stage7Status.stage_progress
                    ? ` (${stage7Status.stage_progress.done}/${stage7Status.stage_progress.total})` : '')}
            </p>
          )}
        </div>

        <div className="sdr-stage7-panel">
          <div className="sdr-stage7-row">
            <span className="sdr-stage7-label">Find missing "get all" endpoints</span>
            <button className="sdr-btn sdr-btn-stage7" disabled={missingRunning} onClick={handleFindMissingGetAll}>
              {missingRunning ? 'Checking…' : 'Check already-captured specs'}
            </button>
          </div>
          <p className="sdr-hint">
            Checks every domain ever captured for this vendor/product (not just what's shown
            below) for a GET that needs no id or name to return everything — the "Retrieve
            All X" shape — that config has never picked up. Reads only what's already saved
            in storage; no live probe run, no vendor API call, works even without a live
            Dev Hub session. Fill in the customer_id field above first (same one Run Stage 7
            and Raw Hit use) to also see what that tenant's own last probe run found for each
            row — permission-denied, works, not found — instead of a blank "never checked".
          </p>
          {missingError && <div className="sdr-error">{missingError}</div>}
          {missingResult && (
            <>
              <div className="sdr-stage7-row">
                <p className="sdr-stage7-status">
                  {missingResult.captured_specs_count} captured domain(s) checked — found{' '}
                  {missingResult.missing_count} "get all" endpoint(s) config has never seen.
                  {missingResult.classifications_checked
                    ? ' Cross-referenced against this tenant’s last probe run.'
                    : ' Fill in customer_id and re-check to see per-row live results.'}
                </p>
                {missingResult.missing_count > 0 && (
                  <button
                    className="sdr-btn sdr-btn-stage7"
                    onClick={() => _exportRowsToExcel(
                      `missing-get-all-siblings-${selected.vendor}-${selected.product}.xlsx`,
                      missingResult.missing,
                      'Missing Get-All',
                    )}
                  >
                    Export to Excel
                  </button>
                )}
              </div>
              {missingResult.missing_count > 0 && (
                <table className="sdr-table">
                  <thead>
                    <tr>
                      <th>Method</th><th>Path</th><th>Area</th><th>Spec name</th>
                      {missingResult.classifications_checked && <th>Last probed as</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {missingResult.missing.map(row => (
                      <tr key={`${row.method}:${row.path}`}>
                        <td className="sdr-mono">{row.method}</td>
                        <td className="sdr-mono sdr-path">{row.path}</td>
                        <td>{row.functional_area}</td>
                        <td className="sdr-mono">{row.name}</td>
                        {missingResult.classifications_checked && (
                          <td>
                            {row.last_classification ? (
                              <div className="sdr-stage7-cell">
                                <span className="sdr-stage7-badge">{row.last_classification}</span>
                                {row.last_note && <span className="sdr-diff-plain">{row.last_note}</span>}
                              </div>
                            ) : (
                              <span className="sdr-hint" style={{ margin: 0 }}>never probed</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        <div className="sdr-stage7-panel">
          <div className="sdr-stage7-row">
            <span className="sdr-stage7-label">Raw Hit — call one endpoint directly</span>
            <input
              type="text"
              className="sdr-stage7-input"
              placeholder="/v1/work/form_profiles"
              value={rawHitPath}
              onChange={e => setRawHitPath(e.target.value)}
              disabled={rawHitRunning}
            />
            <button className="sdr-btn sdr-btn-stage7" disabled={rawHitRunning} onClick={handleRawHit}>
              {rawHitRunning ? 'Calling…' : 'Send GET'}
            </button>
          </div>
          <p className="sdr-hint">
            Calls one path directly against the customer picked above (same field Run Stage 7
            uses), GET only — the real HTTP status and response, no probe conversation. Use
            this to check whether an endpoint from the Missing Get-All list above is actually
            enabled for this tenant (a 403/404/"feature disabled" response answers that in one
            call) instead of guessing from a classification that was never recorded.
          </p>
          {rawHitError && <div className="sdr-error">{rawHitError}</div>}
          {rawHitResult && (
            rawHitResult.error ? (
              <div className="sdr-error">{rawHitResult.error}</div>
            ) : (
              <div className="sdr-stage7-status">
                <p><strong>{rawHitResult.method} {rawHitResult.url}</strong></p>
                <p>HTTP {rawHitResult.http_status}{rawHitResult.supports_pagination ? ' (paginated: page=1&per_Page=3)' : ''}</p>
                <pre className="sdr-mono" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginTop: 8 }}>
                  {rawHitResult.raw_response}
                </pre>
              </div>
            )
          )}
        </div>

        <div className="sdr-stage7-panel">
          <div className="sdr-stage7-row">
            <span className="sdr-stage7-label">Auto-promote GET-all matches</span>
            <button className="sdr-btn sdr-btn-stage7" disabled={autoPromoteRunning} onClick={() => handleAutoPromote(false)}>
              {autoPromoteRunning ? 'Checking…' : 'Preview'}
            </button>
            <button className="sdr-btn sdr-btn-promote" disabled={autoPromoteRunning} onClick={() => handleAutoPromote(true)}>
              {autoPromoteRunning ? 'Promoting…' : 'Promote all matches'}
            </button>
          </div>
          <p className="sdr-hint">
            Scott's ruling, item 158: the one exception to "promotion is always one
            endpoint at a time." Promotes every endpoint that is BOTH live-proven (a real
            2xx observed in the last live run against this connection) AND the trivial
            "get everything, no id or name needed" shape — never a partial match on just
            one of those two. Each still goes through the same structural check and git
            commit as an ordinary Promote click; this button just stops doing it one row
            at a time. "Preview" reports what WOULD be promoted and writes nothing.
          </p>
          {autoPromoteError && <div className="sdr-error">{autoPromoteError}</div>}
          {autoPromoteResult && (
            <>
              <p className="sdr-stage7-status">
                {autoPromoteResult.candidates_found} matching endpoint(s) found.{' '}
                {autoPromoteResult.dry_run
                  ? 'Click "Promote all matches" to actually commit them.'
                  : `${autoPromoteResult.promoted_count} promoted. `
                    + (autoPromoteResult.failed.length > 0
                      ? `${autoPromoteResult.failed.length} failed — see below.`
                      : 'Diff refreshed below.')}
              </p>
              {autoPromoteResult.failed.length > 0 && (
                <table className="sdr-table">
                  <thead><tr><th>Method</th><th>Path</th><th>Error</th></tr></thead>
                  <tbody>
                    {autoPromoteResult.failed.map(row => (
                      <tr key={`${row.method}:${row.path}`}>
                        <td className="sdr-mono">{row.method}</td>
                        <td className="sdr-mono sdr-path">{row.path}</td>
                        <td>{String(row.error)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        {selected.product === 'UKG_PRO_WFM_REST' && (
          <div className="sdr-stage7-panel">
            <div className="sdr-stage7-row">
              <span className="sdr-stage7-label">Merge IA Candidates</span>
              <button
                className="sdr-btn sdr-btn-stage7"
                disabled={mergeRunning}
                onClick={() => handleMergeIaCandidates(false)}
              >
                {mergeRunning ? 'Running…' : 'Dry run'}
              </button>
              <button
                className="sdr-btn sdr-btn-promote"
                disabled={mergeRunning}
                onClick={() => handleMergeIaCandidates(true)}
              >
                {mergeRunning ? 'Running…' : 'Merge for real'}
              </button>
            </div>
            <p className="sdr-hint">
              Generates candidate declarations for UKG WFM's Information Access entities
              (<code>wfm_ia_entity_catalog.json</code>, register #88) never hand-adjudicated
              into an endpoint, and merges them into the live probe emission so they show up
              as New rows below, each individually Promote/Ignore-able. "Dry run" reports
              what WOULD be added and writes nothing; "Merge for real" actually uploads it.
              Never touches <code>config/schemas/</code> itself — only Promote below does that.
            </p>
            {mergeError && <div className="sdr-error">{mergeError}</div>}
            {mergeResult && (
              <p className="sdr-stage7-status">
                {mergeResult.dry_run ? 'Dry run: would add ' : 'Written: added '}
                {mergeResult.added.length} of {mergeResult.candidates_generated} generated
                candidate(s) ({mergeResult.live_endpoints_before} → {mergeResult.live_endpoints_after}{' '}
                endpoints){mergeResult.skipped_collision.length > 0
                  && ` — ${mergeResult.skipped_collision.length} name collision(s) skipped: `
                  + `${mergeResult.skipped_collision.join(', ')}`}.
                {mergeResult.dry_run ? ' Click "Merge for real" to actually write this.' : ' Diff refreshed below.'}
              </p>
            )}
          </div>
        )}

        {error && <div className="sdr-error">{error}</div>}
        {loading && <div className="sdr-loading"><div className="sdr-spinner" /><p>Computing diff…</p></div>}

        {!loading && diff && (
          <>
            <div className="sdr-counts">
              <div className="sdr-count-card sdr-count-new">
                <div className="sdr-count-value">{diff.counts.new}</div>
                <div className="sdr-count-label">New</div>
              </div>
              <div className="sdr-count-card sdr-count-changed">
                <div className="sdr-count-value">{diff.counts.changed}</div>
                <div className="sdr-count-label">Changed</div>
              </div>
              <div className="sdr-count-card sdr-count-unchanged">
                <div className="sdr-count-value">{diff.counts.unchanged}</div>
                <div className="sdr-count-label">Unchanged</div>
              </div>
              <div className="sdr-count-card sdr-count-notseen">
                <div className="sdr-count-value">{diff.counts.not_seen_in_probe}</div>
                <div className="sdr-count-label">Not in live capture</div>
              </div>
            </div>
            <p className="sdr-source-note">
              config: <code>{diff.config_path}</code> · live capture: <code>{diff.probe_storage_path}</code>
              {diff.probe_metadata?.probed_date && <> · probed {diff.probe_metadata.probed_date}</>}
            </p>

            <div className="sdr-note-input">
              <input
                type="text"
                placeholder="Optional note attached to the next Promote/Ignore decision…"
                value={noteDraft}
                onChange={e => setNoteDraft(e.target.value)}
              />
            </div>

            {(diff.new.length > 0 || diff.changed.length > 0) && (
              <div className="sdr-filter-bar">
                <input
                  type="text"
                  className="sdr-filter-input"
                  placeholder="Filter New/Changed by path or probe name…"
                  value={rowSearch}
                  onChange={e => setRowSearch(e.target.value)}
                />
                <select
                  className="sdr-filter-select"
                  value={outcomeFilter}
                  onChange={e => setOutcomeFilter(e.target.value)}
                >
                  <option value="all">Every outcome</option>
                  <option value="resolved">Stage 7: resolved</option>
                  <option value="exhausted">Stage 7: exhausted</option>
                  <option value="blocked">Stage 7: blocked</option>
                  <option value="stuck">Stage 7: stuck</option>
                  <option value="accepted_empty">Stage 7: accepted_empty</option>
                  <option value="no_params_required">Stage 7: no_params_required</option>
                  <option value="not_probed">Stage 7: not yet probed</option>
                  <option value="not_applicable">No body needed (GET)</option>
                </select>
                {(rowSearch || outcomeFilter !== 'all') && (
                  <button className="sdr-btn sdr-btn-clear-filter" onClick={() => { setRowSearch(''); setOutcomeFilter('all') }}>
                    Clear filter
                  </button>
                )}
              </div>
            )}

            {diff.new.length > 0 && (
              <div className="sdr-section">
                <div className="sdr-stage7-row">
                  <h2 className="sdr-section-title">
                    New — in the live capture, not in config
                    {' '}({filteredNew.length === diff.new.length ? diff.new.length : `${filteredNew.length} of ${diff.new.length}`})
                  </h2>
                  <button
                    className="sdr-btn sdr-btn-stage7"
                    onClick={() => _exportRowsToExcel(
                      `schema-diff-new-${selected.vendor}-${selected.product}.xlsx`,
                      filteredNew.map(_flattenDiffRowForExport),
                      'New',
                    )}
                  >
                    Export to Excel
                  </button>
                </div>
                <table className="sdr-table">
                  <thead><tr><th>Method</th><th>Path</th><th>Probe name</th><th>Stage 7</th><th>Action</th></tr></thead>
                  <tbody>
                    {filteredNew.map(row => (
                      <tr key={rowKey(row)}>
                        <td className="sdr-mono">{row.method}</td>
                        <td className="sdr-mono sdr-path">{row.path}</td>
                        <td className="sdr-mono">{row.probe_name}</td>
                        <td><Stage7Badge info={stage7Info(row)} /></td>
                        <td>
                          <ActionButtons
                            row={row}
                            decidedState={decidedByKey[rowKey(row)]}
                            busy={busyKey === rowKey(row)}
                            onDecide={handleDecide}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {diff.changed.length > 0 && (
              <div className="sdr-section">
                <div className="sdr-stage7-row">
                  <h2 className="sdr-section-title">
                    Changed — same endpoint, different attributes
                    {' '}({filteredChanged.length === diff.changed.length ? diff.changed.length : `${filteredChanged.length} of ${diff.changed.length}`})
                  </h2>
                  <button
                    className="sdr-btn sdr-btn-stage7"
                    onClick={() => _exportRowsToExcel(
                      `schema-diff-changed-${selected.vendor}-${selected.product}.xlsx`,
                      filteredChanged.map(_flattenDiffRowForExport),
                      'Changed',
                    )}
                  >
                    Export to Excel
                  </button>
                </div>
                <table className="sdr-table">
                  <thead><tr><th>Method</th><th>Path</th><th>Diffs</th><th>Stage 7</th><th>Action</th></tr></thead>
                  <tbody>
                    {filteredChanged.map(row => (
                      <tr key={rowKey(row)}>
                        <td className="sdr-mono">{row.method}</td>
                        <td className="sdr-mono sdr-path">{row.path}</td>
                        <td><DiffsList diffs={row.diffs} diffsPlain={row.diffs_plain} /></td>
                        <td><Stage7Badge info={stage7Info(row)} /></td>
                        <td>
                          <ActionButtons
                            row={row}
                            decidedState={decidedByKey[rowKey(row)]}
                            busy={busyKey === rowKey(row)}
                            onDecide={handleDecide}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {diff.not_seen_in_probe.length > 0 && (
              <details className="sdr-section sdr-collapsible">
                <summary className="sdr-section-title">
                  Not in live capture — in config, the probe run didn't exercise it ({diff.not_seen_in_probe.length})
                </summary>
                <p className="sdr-hint">
                  Not a removal — the probe run only exercises what it can reach in one pass.
                  No action available here; re-run the probe with wider scope to get a real
                  signal on these.
                </p>
                <table className="sdr-table">
                  <thead><tr><th>Method</th><th>Path</th><th>Config name</th></tr></thead>
                  <tbody>
                    {diff.not_seen_in_probe.map(row => (
                      <tr key={rowKey(row)}>
                        <td className="sdr-mono">{row.method}</td>
                        <td className="sdr-mono sdr-path">{row.path}</td>
                        <td className="sdr-mono">{row.config_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}

            {decisions.length > 0 && (
              <div className="sdr-section">
                <h2 className="sdr-section-title">Decision history ({decisions.length})</h2>
                <table className="sdr-table">
                  <thead><tr><th>When</th><th>Method</th><th>Path</th><th>Decision</th><th>By</th><th>Structural check</th></tr></thead>
                  <tbody>
                    {[...decisions].reverse().map((d, i) => (
                      <tr key={i}>
                        <td className="sdr-mono">{new Date(d.decided_at).toLocaleString()}</td>
                        <td className="sdr-mono">{d.method}</td>
                        <td className="sdr-mono sdr-path">{d.path}</td>
                        <td className={`sdr-decided-${d.decision}`}>{d.decision}</td>
                        <td>{d.decided_by}</td>
                        <td className="sdr-mono">
                          {d.structural_roundtrip_ok === null ? '—' : (d.structural_roundtrip_ok ? '✓ passed' : '✗ failed')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
